import React, { useState, useEffect } from "react";
import { Box, Typography, Grid, List, ListItem, TextField, styled, Stack, Card } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import NoData from "../../../components/noData/noData";
import NotesListSkeleton from "./NotesListSkeleton";
import { useTranslation } from "react-i18next";
import Icon from "../../../components/Icon/Icon";
import ItemLabel from "../../../components/ItemLabel/ItemLabel";

const NoteTextField = styled(TextField)({
  borderRadius: '2px !important'
});


const Notes = ({
  uniqueKey,
  noteContent,
  setNoteContent,
  notesData,
  isLoading,
  noNoteExistMessage,
  onNoteChange,
  onAddNote,
  formatDateTime,
  classes,
  imageArray,
  externalAddOpen,
  onExternalAddClose,
}) => {
  const { t } = useTranslation();
  const [showTextField, setShowTextfield] = useState(false);
  const [showAddButton, setShowAddButton] = useState(true);

  useEffect(() => {
    if (externalAddOpen) {
      setShowTextfield(true);
      setShowAddButton(false);
    }
  }, [externalAddOpen]);

  const handleCancelNotes = () => {
    setShowTextfield(false);
    setShowAddButton(true);
    setNoteContent("");
    onExternalAddClose?.();
  }
  return (
    <>
      {showTextField && <NoteTextField
        multiline
        inputProps={{ maxLength: 1000 }}
        aria-label="minimum height"
        minRows={5}
        label="Type note"
        value={noteContent}
        onChange={onNoteChange}
        className={classes['custom-patient-note-textfield']}
        sx={{ marginTop: 4, width: '100%' }}
      />}
      {showTextField && (
        <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ gap: 2, mt: 3 }}>
          <Button
            type="reset"
            color='secondary'
            onClick={handleCancelNotes}
          >
            {t("Prescriptions.cancel")}
          </Button>
          <Button
            type="submit"
            color="primary"
            onClick={() => {
              if (noteContent.trim()) {
                onAddNote();
              }
              setShowTextfield(false);
              setShowAddButton(true);
            }}
            disabled={!noteContent?.trim()}
          >
            {t("PatientNote.saveNotes")}
          </Button>
        </Stack>
      )}
      <Stack direction="row" justifyContent={{ xs: 'flex-start', md: 'flex-end' }} sx={{ ml: 'auto', mb: 5 }}>
        {showAddButton && <Button
          sx={{ display: { xs: 'none', md: 'flex' } }}
          color="primary"
          data-testid='add-Note-Button'
          onClick={() => {
            setShowTextfield(true);
            setShowAddButton(false);
          }}
          startIcon={<Icon icon="fa-plus" />}
        >
          {t("PatientNote.button")}
        </Button>}
      </Stack>
      {notesData?.length > 0 ? (
        <Card>
          <List>
            {notesData.map((item, index) => (
              <ListItem
                key={uniqueKey === 'ordersNotes' ? item.patient_preauth_form_id : item.order_number}
                divider
                sx={{ py: 4, alignItems: 'flex-start' }}
              >
                {(uniqueKey === 'ordersNotes' ? item.note !== "" : item.note_content !== "") && (
                  <Grid container>
                    <Grid item md={1} xs={3}>
                      {uniqueKey === 'ordersNotes' ?
                        (item.profile_image ?
                          (imageArray.length > 0 ?
                            <Box component="img" src={`data:data:image/jpeg;base64,${imageArray.find(value => value.imagejpg == item.profile_image)?.image}`} alt="User Profile" sx={{ height: 50, width: 50, borderRadius: '50%' }} />
                            :
                            <Avatar sx={{ height: 50, width: 50 }} />
                          ) :
                          <Avatar sx={{ height: 50, width: 50 }} imageUrl={`data:data:image/jpeg;base64,${imageArray.find(value => value.imagejpg == item.profile_image)?.image}`} />
                        ) :
                        (item.profile_image_url ?
                          <Box component="img" src={`data:image/png;base64,${item.profile_image_url}`} alt="User Profile" sx={{ height: 50, width: 50, borderRadius: '50%' }} />
                          :
                          <Avatar sx={{ height: 50, width: 50 }} />
                        )
                      }
                    </Grid>
                    <Grid item md={11} xs={9}>
                      {uniqueKey === 'ordersNotes' ?
                        <>
                          <Typography component="span" variant="h6">
                            {item.user_last_name},{' '}
                            {item.user_first_name}{' '}
                            {item.user_middle_name}{' '}
                          </Typography>
                          <Typography component="span" color="text.secondary">{formatDateTime(item.created_on)}{' '}</Typography>
                        </> :
                        <>
                          <Typography component="span" variant="h6" fontWeight={600}> {item.last_name}{","}{" "}
                            {item.first_name}{" "}
                            {item.middle_initial}{" "}
                          </Typography>
                          <Typography component="span" color="text.secondary">{item?.added_on_formatted}{" "}</Typography>
                          {item.order_number !== 0 && <ItemLabel label={t("PatientNote.order")} text={item.order_number} />}
                        </>
                      }
                      <Box sx={{ wordBreak: 'break-word', width: '100%' }}>
                        <Box sx={{ whiteSpace: "pre-line" }}>
                          {uniqueKey === 'ordersNotes' ?
                            (item.note.split("\n").map((line, index) => (<div key={item.patient_preauth_form_id}>{line} </div>))) :
                            (item.note_content?.split("\n").map((line, index) => (<div key={item.order_number}>{line}</div>)))}
                        </Box>
                      </Box>
                      <Typography sx={{ wordBreak: 'break-word', width: '100%' }}>
                        {item.description}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </ListItem>
            ))}
          </List>
        </Card>
      ) : isLoading ? (
        <NotesListSkeleton />
      ) : (
        <Card>
            <NoData message={noNoteExistMessage} />
        </Card>
      )}
    </>
  );
};

export default Notes;
